import React from 'react';
import Grid from '@mui/material/Grid';
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { IconButton, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormHelperText, Tooltip, Box, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ConfirmModal from "../../../common/ConfirmModal";
import { API } from "aws-amplify";
import AddEditPersonnel from "../../../components/AddEditPersonnel";
import FileDownload from "../../../common/FileDownload";
import Importer from "../../../common/Importer";
import TableDisplay from '../../../common/TableDisplay';
import ImportPreviewModal from "../../../components/ImportPreviewModal";
import { isPersonnelImportV2Enabled } from "../../../shared/access";

const useStyles = makeStyles(theme => ({
    root: {
        width: '90%',
    },
    button: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
        float: 'right'
    },
    actionsContainer: {
        marginBottom: theme.spacing(2),
    },
    resetContainer: {
        padding: theme.spacing(3),
    },
    completed: {
        color: theme.palette.success[600] + "!important",
    },
    searchAndNavigation: {
        '& button[aria-label="Clear Search"]': {
            color: 'var(--colorTitle)',
            '&.Mui-disabled': {
                color: 'var(--colorSubtitle)',
            },
        },
        '& input::placeholder': {
            color: 'var(--colorSubtitle)',
            opacity: 1,
        },
        '& .MuiTablePagination-root .MuiIconButton-root': {
            color: 'var(--colorTitle)',
        },
        '& .MuiTablePagination-root .MuiSelect-icon': {
            color: 'var(--colorTitle)',
        }
    }
}));

const renderQrCode = rowData => {
    return (
        <FileDownload path={"/api/v1/personnel/" + rowData.id + "/qrcode"}>
            <IconButton color="primary" aria-label="Download QR Code" component="span">
                <GetAppIcon />
            </IconButton>
        </FileDownload>
    );
}

const columns = [
    { id: 'name', label: 'Personnel Name', minWidth: 170, title: 'Personnel Name', field: 'name', cellStyle: { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 270}, 
      customSort: (a,b) => a?.name?.localeCompare(b?.name), },
    { id: 'email', label: 'Email', minWidth: 170, title: 'Email', field: 'email', customSort: (a,b) => a?.email?.localeCompare(b?.email), },
    { id: 'personnelId', label: 'Connector ID', minWidth: 170, title: 'Connector ID', field: 'personnelId', sorting: false, headerStyle: {cursor:'default'} },
    { id: 'type', label: 'Type', minWidth: 170, title: 'Type', field: 'type', },
    { id: 'qrcode', label: 'QR Code', minWidth: 50, title: 'QR Code', field: 'qrcode', render: renderQrCode, sorting: false, headerStyle: {cursor:'default'} },
];

function PersonnelIndex(props) {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [importSchema, setImportSchema] = React.useState([]);
    const [personnel, setPersonnel] = React.useState([]);

    // EN-21290: Check feature flag
    const importV2Enabled = isPersonnelImportV2Enabled();

    // EN-21290: Import mode and preview state
    const [importMode, setImportMode] = React.useState('PARTIAL_UPDATE');
    const [showPreview, setShowPreview] = React.useState(false);
    const [previewData, setPreviewData] = React.useState(null);
    const [pendingImportData, setPendingImportData] = React.useState(null);
    const [importLoading, setImportLoading] = React.useState(false);
    const [hasActiveFilters, setHasActiveFilters] = React.useState(false);
    const loadPersonnel = () => {
        API.get("Core", "/api/v1/personnel")
            .then(response => {
                setPersonnel(response.map(p => { return { ...p, email: p.email || '' , 'actions': true } }));
            },
                error => {
                    toast("Could not get personnel: " + error.response.data.message, { position: "top-center", type: "error" });
                })
    }

    const loadSchema = () => {
        API.get("Core", "/api/v1/personnel/csv/schema")
            .then(response => {
                setImportSchema(response);
            },
                error => {
                    toast("Could not get schema: " + error.response.data.message, { position: "top-center", type: "error" });
                })
    }

    React.useEffect(() => {
        loadPersonnel();
        loadSchema();
    }, [])

    const deletePersonnel = (personnelId) => {
        API.del('Core', '/api/v1/personnel/' + personnelId)
            .then(response => {
                toast("Deleted Personnel!", { position: "top-center", type: "success" });
                loadPersonnel();
            },
                error => {
                    toast("Could not Delete Personel" + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const renderActions = rowData => {
        let flexContainer = {display:'flex'};
        let flexChild = {flex:1};

        return (
            <div style={flexContainer}>
                <div style={flexChild}>
                <AddEditPersonnel button={(
                    <IconButton color="primary">
                        <EditIcon />
                    </IconButton>
                )} personnelId={rowData.id} onSuccess={() => { loadPersonnel(); }} />
                </div>
                <div style={flexChild}>
                <ConfirmModal
                    title={"Delete Personnel"}
                    text={(<span>Are you sure you want to delete <b>{rowData.name}</b>?</span>)}
                    onConfirm={deletePersonnel.bind(this, rowData.id)}
                    confirmTitle={"Delete"}
                >
                    <IconButton color="primary">
                        <DeleteIcon />
                    </IconButton>
                </ConfirmModal>
                </div>
            </div>);
    }

    // EN-21290: REQ #4 - Preview import before committing
    const handleImportPreview = (csvData, importerInstance) => {
        API.post("Core", "/api/v1/personnel/csv/preview", {
            body: csvData,
            queryStringParameters: { mode: importMode }
        }).then(response => {
            setPreviewData(response);
            setPendingImportData(csvData);
            setShowPreview(true);
            // Close the Flatfile importer UI since we're showing our own preview modal
            if (importerInstance) {
                importerInstance.close();
            }
        }).catch(err => {
            // EN-21290: Provide actionable error messages based on status code
            let errorMsg = "Preview failed";
            const statusCode = err.response?.status;

            if (statusCode === 400) {
                // Validation error - user needs to fix CSV
                errorMsg = "Invalid CSV data: " + (err.response?.data?.message || "Please check your file format and data");
            } else if (statusCode === 403 || statusCode === 401) {
                // Feature not enabled or permission issue
                errorMsg = "Feature not enabled for this tenant or insufficient permissions";
            } else if (statusCode === 413) {
                // File too large
                errorMsg = "CSV file too large. Please split into smaller files (max 5000 rows)";
            } else if (statusCode >= 500) {
                // Server error - user should contact support
                errorMsg = "Server error: " + (err.response?.data?.message || "Please contact support if this persists");
            } else {
                // Generic error
                errorMsg = err.response?.data?.message || err.message || "An unexpected error occurred";
            }

            toast(errorMsg, { position: "top-center", type: "error" });
            // Show error in Flatfile UI
            if (importerInstance) {
                importerInstance.displayError(errorMsg);
            }
        });
    };

    // EN-21290: REQ #4 - User confirms preview and executes import
    const handleImportConfirm = () => {
        if (!pendingImportData) {
            toast("No import data to confirm", { position: "top-center", type: "error" });
            return;
        }

        setImportLoading(true);

        API.post("Core", "/api/v1/personnel/csv", {
            body: pendingImportData,
            queryStringParameters: {
                mode: importMode,
                fileName: 'import.csv'
            }
        }).then(response => {
            toast(`Import successful: ${response.rowsUpdated} rows updated`, { position: "top-center", type: "success" });
            setShowPreview(false);
            setPendingImportData(null);
            loadPersonnel();
        }).catch(err => {
            // EN-21290: Provide actionable error messages based on status code
            let errorMsg = "Import failed";
            const statusCode = err.response?.status;

            if (statusCode === 400) {
                errorMsg = "Invalid data: " + (err.response?.data?.message || "Please verify your CSV data");
            } else if (statusCode === 403 || statusCode === 401) {
                errorMsg = "Permission denied or feature not enabled";
            } else if (statusCode === 413) {
                errorMsg = "CSV file too large. Maximum 5000 rows allowed";
            } else if (statusCode >= 500) {
                errorMsg = "Server error: " + (err.response?.data?.message || "Contact support if this persists");
            } else {
                errorMsg = err.response?.data?.message || err.message || "An unexpected error occurred";
            }

            toast(errorMsg, { position: "top-center", type: "error" });
        }).finally(() => {
            setImportLoading(false);
        });
    };

    // EN-21290: REQ #6 - Export warning (conditional based on filter state)
    const handleExportClick = () => {
        // Only show warning if filters are active
        if (hasActiveFilters) {
            return window.confirm("Export will include ALL personnel in the database. Active filters will be ignored. Continue?");
        }
        return true; // No warning needed when no filters active
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                {/* EN-21290: Import Mode Selection (REQ #5) - Only show if V2 enabled */}
                {importV2Enabled && (
                    <FormControl component="fieldset" style={{ marginBottom: 16, marginTop: 16 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <FormLabel component="legend">Import Mode</FormLabel>
                            <Tooltip
                                title={
                                    <Box sx={{ p: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                                            Partial Update (Recommended):
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                                            <strong>• Fewer rows in CSV:</strong> Only the personnel in your CSV are updated. Others remain unchanged.
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                                            <strong>• Same number of rows:</strong> Only matching personnel are updated.
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.8125rem' }}>
                                            <strong>• Extra rows in CSV:</strong> Only existing personnel are updated. New entries are <strong>ignored</strong> (not created).
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'warning.main' }}>
                                            Full Replace (Creates New Personnel):
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                                            <strong>• Fewer rows in CSV:</strong> CSV personnel are updated. Others in database remain unchanged (NOT deleted).
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                                            <strong>• Same number of rows:</strong> Matching personnel are updated. No deletions occur.
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                            <strong>• Extra rows in CSV:</strong> Existing personnel updated AND new personnel are <strong>created</strong> from extra rows.
                                        </Typography>
                                    </Box>
                                }
                                arrow
                                placement="right"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: 'background.paper',
                                            color: 'text.primary',
                                            boxShadow: 3,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            maxWidth: 450,
                                            '& .MuiTooltip-arrow': {
                                                color: 'background.paper',
                                                '&::before': {
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{ color: 'primary.main' }}
                                    aria-label="Help: Detailed explanation of import modes"
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <RadioGroup
                            row
                            value={importMode}
                            onChange={(e) => setImportMode(e.target.value)}
                        >
                            <FormControlLabel
                                value="PARTIAL_UPDATE"
                                control={<Radio />}
                                label="Partial (update only existing personnel)"
                            />
                            <FormControlLabel
                                value="FULL_REPLACE"
                                control={<Radio />}
                                label="Full Replace (update existing + create new)"
                            />
                        </RadioGroup>
                        <FormHelperText>
                            {importMode === 'FULL_REPLACE'
                                ? '⚠️ Full mode will create NEW personnel from CSV. Existing personnel NOT in CSV remain unchanged.'
                                : 'Recommended: Only updates personnel present in your CSV file. New entries are ignored.'}
                        </FormHelperText>
                    </FormControl>
                )}

                <AddEditPersonnel
                    button={(<Button variant="contained" color="primary" className={classes.button}>
                        Add Personnel
                    </Button>)}
                    onSuccess={() => { loadPersonnel(); }}
                />
                <FileDownload
                    path={"/api/v1/personnel/csv"}
                    onClick={importV2Enabled ? handleExportClick : undefined}
                >
                    <Button variant="contained" color="primary" className={classes.button}>
                        Export Personnel
                    </Button>
                </FileDownload>
                <Importer
                    title={"Personnel"}
                    fields={importSchema}
                    importPath={"/api/v1/personnel/csv"}
                    onSuccess={() => {
                        loadPersonnel()
                    }}
                    onBeforeSubmit={importV2Enabled ? handleImportPreview : undefined}
                >
                    <Button variant="contained" color="primary" className={classes.button}>
                        Import Personnel
                    </Button>
                </Importer>

                {/* EN-21290: Import Preview Modal (REQ #4) - Only show if V2 enabled */}
                {importV2Enabled && (
                    <ImportPreviewModal
                        open={showPreview}
                        data={previewData}
                        importMode={importMode}
                        loading={importLoading}
                        onConfirm={handleImportConfirm}
                        onCancel={() => {
                            setShowPreview(false);
                            setPendingImportData(null);
                        }}
                    />
                )}
            </Grid>
            <Grid xs={12}>
                <div className={classes.searchAndNavigation}>
                <TableDisplay columns={[...columns, { title: 'Actions', field: 'actions', render: renderActions, sorting: false, headerStyle: {cursor:'default'} }]}
                    title='Personnel'
                    options={{thirdSortClick: false}}
                    data={personnel} pageSizeOptions={[10, 25, 50]}
                    style={{ paddingLeft: '16px' }}
                    onStateChange={(state) => {
                        // EN-21290: REQ #6 - Track if any filters are active
                        const hasFilters = !!(state?.globalFilter || state?.columnFilters?.length > 0 || state?.sorting?.length > 0);
                        setHasActiveFilters(hasFilters);
                    }} />
                  </div>
            </Grid>
        </Grid>
    )
}

export default PersonnelIndex;